import { Fragment, useEffect, useRef, useState } from 'react';
import { Box, Divider, Grid, Typography, useTheme } from '@mui/material';
import { ApplicationProps } from '../../redux-store/types/api-types';
import { KeyValue } from './personalDetails';
import { Item, ItemDetails } from './index';

const getChangeRequestDisplay = (value: string | null | undefined): string => {
    return value === '1' ? 'Yes' : 'No';
};

export default function KycStatusInfo({ application }: { application: ApplicationProps }): JSX.Element {
    const theme = useTheme();

    const kycModificationStatusList = application?.kycModificationStatusList ?? [];

    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleCopyUrl = async (url: string | null | undefined, index: number) => {
        try {
            if (!url) return;

            await navigator.clipboard.writeText(url);
            setCopiedIndex(index);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                setCopiedIndex(null);
                timeoutRef.current = null;
            }, 1500);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    return (
        <Box
            sx={{
                bgcolor: 'background.paper',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                borderRadius: '10px',
                border: `1px solid ${theme.palette.grey[100]}`,
            }}
            mt={5}
            mb={5}
        >
            <Box sx={{ p: "25px 0px" }}>
                <Grid container sx={{ px: { xs: 1, sm: 4 } }}>
                    <Grid item xs={12}>
                        <Typography
                            variant="body_700"
                            sx={{
                                color: theme.palette.primary[900],
                                mb: 3,
                            }}
                        >
                            KYC Status Details
                        </Typography>
                    </Grid>

                    {kycModificationStatusList.map((data: any, index: number) => (
                        <Fragment key={index}>
                            <Grid item container rowSpacing={3} columnSpacing={5} sx={{ mb: index < kycModificationStatusList.length - 1 ? 3 : 0 }}>
                                <KeyValue title="Name" description={data?.name} />
                                <KeyValue title="PAN" description={data?.panNo} />
                                <KeyValue title="Email Id" description={data?.emailId} />
                                <Grid item xs={12} sm={6}>
                                    <Item sx={{
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        letterSpacing: '0.02px',
                                        color: theme.palette.text.secondary,
                                        pb: '7px',
                                    }}>
                                        KYC URL
                                    </Item>
                                    <ItemDetails
                                        onClick={() => handleCopyUrl((data?.KYCOnboardingShortUrl ?? data?.KYCOnboardingUrl ?? ""), index)}
                                        sx={{
                                            color: (data?.KYCOnboardingShortUrl ?? data?.KYCOnboardingUrl ?? "") ? theme.palette.primary[500] : theme.palette.text.primary,
                                            fontWeight: '500',
                                            cursor: (data?.KYCOnboardingShortUrl ?? data?.KYCOnboardingUrl ?? "") ? 'pointer' : 'default',
                                        }}
                                    >
                                        {(data?.KYCOnboardingShortUrl ?? data?.KYCOnboardingUrl ?? "") ? (
                                            (copiedIndex === index) ? 'Copied' : 'Copy URL'
                                        ) : '-'}
                                    </ItemDetails>
                                </Grid>
                                <KeyValue
                                    title="Name Change Request"
                                    description={getChangeRequestDisplay(data?.isNameChange)}
                                />
                                <KeyValue
                                    title="Email Change Request"
                                    description={getChangeRequestDisplay(data?.isEmailChange)}
                                />
                                <KeyValue
                                    title="Mobile Change Request"
                                    description={getChangeRequestDisplay(data?.isMobileChange)}
                                />
                                <KeyValue
                                    title="Address Change Request"
                                    description={getChangeRequestDisplay(data?.isAddressChange)}
                                />
                                <KeyValue title="KRA KYC Status" description={data?.kraKYCStatus} />
                            </Grid>
                            {index < kycModificationStatusList.length - 1 && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                </Grid>
                            )}
                        </Fragment>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}
